// Enums for workout data
export enum RPE {
  NONE = "none",
  RPE_6 = "~6",
  RPE_7 = "~7",
  RPE_8 = "~8",
  RPE_9 = "~9",
  RPE_10 = "~10"
}

export enum WorkoutDay {
  MONDAY = "Monday",
  TUESDAY = "Tuesday",
  WEDNESDAY = "Wednesday",
  THURSDAY = "Thursday",
  FRIDAY = "Friday",
  SATURDAY = "Saturday",
  SUNDAY = "Sunday"
}

export enum IntensityTechnique {
  NONE = "none",
  DROP_SET = "drop_set",
  REST_PAUSE = "rest_pause",
  CLUSTER_SET = "cluster_set"
}

// Base interfaces
export interface SubstitutionOption {
  name: string;
  link: string;
}

export interface Exercise {
  exercise: string;
  exercise_video: string;
  last_set_intensity_technique: IntensityTechnique | string;
  warmup_sets: string; // e.g., "2-3", "1-2"
  working_sets: string; // e.g., "1", "2"
  reps: string; // e.g., "6-8", "8-10"
  early_set_rpe: RPE | string;
  last_set_rpe: RPE | string;
  rest: string; // e.g., "3-5 min", "1-2 min"
  substitution_option_1: SubstitutionOption;
  substitution_option_2: SubstitutionOption;
  notes: string;
}

export interface DayWorkout {
  [WorkoutDay.MONDAY]: Exercise[] | string;
  [WorkoutDay.TUESDAY]: Exercise[] | string;
  [WorkoutDay.WEDNESDAY]: Exercise[] | string;
  [WorkoutDay.THURSDAY]: Exercise[] | string;
  [WorkoutDay.FRIDAY]: Exercise[] | string;
  [WorkoutDay.SATURDAY]: Exercise[] | string;
  [WorkoutDay.SUNDAY]: Exercise[] | string;
}

export interface WeekProgram {
  [key: string]: DayWorkout; // "1", "2", "3", etc.
}

// Workout tracking interfaces
export interface SetRecord {
  setNumber: number;
  setType: 'warmup' | 'working';
  weight: number;
  reps: number;
  rpe?: RPE;
  actualRpe?: RPE;
  notes?: string;
  completed: boolean;
}

export interface ExerciseRecord {
  exerciseName: string;
  date: string;
  sets: SetRecord[];
  totalVolume: number; // weight * reps * sets
  personalRecord?: boolean;
}

export interface WorkoutSession {
  id: string;
  date: string;
  week: number;
  day: WorkoutDay;
  exercises: ExerciseRecord[];
  duration?: number; // in minutes
  notes?: string;
  completed: boolean;
}

export interface UserProgress {
  userId: string;
  currentWeek: number;
  currentDay: WorkoutDay;
  workoutHistory: WorkoutSession[];
  personalRecords: Record<string, ExerciseRecord>; // exercise name -> best record
}

// Utility types for parsing ranges
export interface SetRange {
  min: number;
  max: number;
}

export interface RepRange {
  min: number;
  max: number;
}

export interface RestRange {
  min: number;
  max: number;
  unit: 'min' | 'sec';
}

// Current workout tracking (in-progress)
export interface CurrentSet {
  setNumber: number;
  type: 'warmup' | 'working';
  weight: number | null;
  reps: number | null;
  actualRpe?: RPE; // User-selected RPE for working sets
  completed: boolean;
}

export interface CurrentExerciseProgress {
  exerciseIndex: number;
  exerciseName: string;
  sets: CurrentSet[];
  completed: boolean;
}

export interface CurrentWorkoutState {
  week: number;
  day: WorkoutDay;
  exercises: CurrentExerciseProgress[];
  startTime: string;
}

// Helper functions type definitions
export interface WorkoutUtils {
  parseSetRange: (sets: string) => SetRange;
  parseRepRange: (reps: string) => RepRange;
  parseRestRange: (rest: string) => RestRange;
  calculateVolume: (weight: number, reps: number, sets: number) => number;
  isPersonalRecord: (current: ExerciseRecord, previous: ExerciseRecord[]) => boolean;
} 